import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Menu, MenuItem, Stack, Tooltip, useMediaQuery, useTheme } from '@mui/material'
import { Check, FilterList } from '@mui/icons-material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import dayjs, { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import i18n from '../../config/i18n'
import { SquareButton } from '../../styles/ButtonStyles'

type Props = {
  startDate: Dayjs | null
  endDate: Dayjs | null
  setStartDate: (date: Dayjs | null) => void
  setEndDate: (date: Dayjs | null) => void
  startDateLabel?: string
  endDateLabel?: string
  suggestedDate?: Dayjs | null
  presetsType?: 'past' | 'future'
  column?: boolean
  minDate?: Dayjs | null
  selectedDate?: string | null
}

const DateRangePickerWithPresets = ({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  startDateLabel,
  endDateLabel,
  suggestedDate = null,
  presetsType = 'past',
  column,
  minDate,
  selectedDate,
}: Props) => {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null)
  const open = Boolean(anchorEl)
  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))

  const presets = useMemo(() => {
    const presetDefinitions = [
      {
        baseKey: 'thisMonth',
        future: {
          key: 'thisMonth',
          name: t('components.dateRangePickerWithPresets.presets.thisMonth'),
          getRange: () => ({
            start: selectedDate ? dayjs(selectedDate).startOf('month') : dayjs(),
            end: dayjs(selectedDate).endOf('month'),
          }),
        },
        past: {
          key: 'thisMonth',
          name: t('components.dateRangePickerWithPresets.presets.thisMonth'),
          getRange: () => ({
            start: dayjs().startOf('month'),
            end: dayjs().endOf('month'),
          }),
        },
      },
      {
        baseKey: 'oneMonth',
        future: {
          key: 'oneMonthFuture',
          name: t('components.dateRangePickerWithPresets.presets.oneMonthFuture'),
          getRange: () => ({
            start: dayjs(selectedDate),
            end: dayjs(selectedDate).add(1, 'month').endOf('day'),
          }),
        },
        past: {
          key: 'oneMonth',
          name: t('components.dateRangePickerWithPresets.presets.oneMonth'),
          getRange: () => ({
            start: dayjs().subtract(1, 'month').startOf('day'),
            end: dayjs().endOf('day'),
          }),
        },
      },
      {
        baseKey: 'monthOffset',
        future: {
          key: 'nextMonth',
          name: t('components.dateRangePickerWithPresets.presets.nextMonth'),
          getRange: () => ({
            start: dayjs(selectedDate).add(1, 'month').startOf('month'),
            end: dayjs(selectedDate).add(1, 'month').endOf('month'),
          }),
        },
        past: {
          key: 'prevMonth',
          name: t('components.dateRangePickerWithPresets.presets.prevMonth'),
          getRange: () => ({
            start: dayjs().subtract(1, 'month').startOf('month'),
            end: dayjs().subtract(1, 'month').endOf('month'),
          }),
        },
      },
      {
        baseKey: 'thisWeek',
        future: {
          key: 'thisWeek',
          name: t('components.dateRangePickerWithPresets.presets.thisWeek'),
          getRange: () => ({
            start: selectedDate ? dayjs(selectedDate).startOf('week') : dayjs(),
            end: dayjs(selectedDate).endOf('week'),
          }),
        },
        past: {
          key: 'thisWeek',
          name: t('components.dateRangePickerWithPresets.presets.thisWeek'),
          getRange: () => ({
            start: dayjs().startOf('week'),
            end: dayjs().endOf('week'),
          }),
        },
      },
      {
        baseKey: 'sevenDays',
        future: {
          key: 'next7Days',
          name: t('components.dateRangePickerWithPresets.presets.next7Days'),
          getRange: () => ({
            start: dayjs(selectedDate),
            end: dayjs(selectedDate).add(7, 'day').endOf('day'),
          }),
        },
        past: {
          key: 'last7Days',
          name: t('components.dateRangePickerWithPresets.presets.last7Days'),
          getRange: () => ({
            start: dayjs().subtract(7, 'day').startOf('day'),
            end: dayjs().endOf('day'),
          }),
        },
      },
      {
        baseKey: 'weekOffset',
        future: {
          key: 'nextWeek',
          name: t('components.dateRangePickerWithPresets.presets.nextWeek'),
          getRange: () => ({
            start: dayjs(selectedDate).add(1, 'week').startOf('week'),
            end: dayjs(selectedDate).add(1, 'week').endOf('week'),
          }),
        },
        past: {
          key: 'prevWeek',
          name: t('components.dateRangePickerWithPresets.presets.prevWeek'),
          getRange: () => ({
            start: dayjs().subtract(1, 'week').startOf('week'),
            end: dayjs().subtract(1, 'week').endOf('week'),
          }),
        },
      },
      {
        baseKey: 'twoWeeks',
        future: {
          key: 'next2Weeks',
          name: t('components.dateRangePickerWithPresets.presets.next2Weeks'),
          getRange: () => ({
            start: dayjs(selectedDate).add(1, 'week').startOf('week'),
            end: dayjs(selectedDate).add(2, 'week').endOf('week'),
          }),
        },
        past: {
          key: 'prev2Weeks',
          name: t('components.dateRangePickerWithPresets.presets.prev2Weeks'),
          getRange: () => ({
            start: dayjs().subtract(2, 'week').startOf('week'),
            end: dayjs().subtract(1, 'week').endOf('week'),
          }),
        },
      },
      {
        baseKey: 'fourteenDays',
        future: {
          key: 'next14Days',
          name: t('components.dateRangePickerWithPresets.presets.next14Days'),
          getRange: () => ({
            start: dayjs(selectedDate),
            end: dayjs(selectedDate).add(13, 'day').endOf('day'),
          }),
        },
        past: {
          key: 'last14Days',
          name: t('components.dateRangePickerWithPresets.presets.last14Days'),
          getRange: () => ({
            start: dayjs().subtract(13, 'day').startOf('day'),
            end: dayjs().endOf('day'),
          }),
        },
      },
    ]

    return presetDefinitions.map(def => {
      const preset = presetsType === 'future' ? def.future : def.past
      return {
        key: preset.key,
        name: preset.name,
        getRange: preset.getRange,
      }
    })
  }, [presetsType, t, selectedDate])

  const getPresetNameForDates = useCallback(
    (start: Dayjs | null, end: Dayjs | null) => {
      if (!start || !end) return null
      const preset = presets.find(
        preset => start.isSame(preset.getRange().start, 'day') && end.isSame(preset.getRange().end, 'day')
      )
      return preset?.key ?? null
    },
    [presets]
  )

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const applyPreset = (presetKey: string) => {
    const foundPreset = presets.find(p => p.key === presetKey)
    if (foundPreset) {
      const range = foundPreset.getRange()
      setStartDate(range.start || startDate)
      setEndDate(range.end || endDate)
      setSelectedPreset(presetKey)
    }
    setAnchorEl(null)
  }

  useEffect(() => {
    if (!startDate || !endDate) {
      setSelectedPreset(null)
      return
    }
    const match = getPresetNameForDates(startDate, endDate)
    setSelectedPreset(match)
  }, [startDate, endDate, getPresetNameForDates])

  return (
    <Stack direction={isDownSm ? 'column' : 'row'} spacing={column ? 1.5 : 3} sx={{ width: '100%' }}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
        {isDownSm ? (
          <Stack spacing={column ? 1.5 : 3} sx={{ width: '100%' }}>
            <DatePicker
              sx={{ width: '100%', svg: { color: 'text.mediumEmphasis' } }}
              slotProps={{ textField: { size: 'small' } }}
              label={startDateLabel ?? t('components.dateRangePickerWithPresets.dateRange.startDate')}
              value={startDate}
              onChange={date => setStartDate(date)}
              minDate={minDate}
              referenceDate={suggestedDate || dayjs()}
            />
            <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
              <DatePicker
                sx={{ flex: 1, svg: { color: 'text.mediumEmphasis' } }}
                slotProps={{ textField: { size: 'small' } }}
                label={endDateLabel ?? t('components.dateRangePickerWithPresets.dateRange.endDate')}
                value={endDate}
                onChange={date => setEndDate(date)}
                minDate={startDate}
                referenceDate={suggestedDate || dayjs()}
              />
              <Tooltip title={t('components.dateRangePickerWithPresets.dateRange.presetsTooltip')}>
                <SquareButton onClick={handleClick} color="secondary">
                  <FilterList />
                </SquareButton>
              </Tooltip>
            </Stack>
          </Stack>
        ) : (
          <Stack direction={column ? 'column' : 'row'} spacing={column ? 2 : 3} sx={{ width: '100%' }}>
            <DatePicker
              sx={{ width: '100%', svg: { color: 'text.mediumEmphasis' } }}
              slotProps={{ textField: { size: 'small' } }}
              label={startDateLabel ?? t('components.dateRangePickerWithPresets.dateRange.startDate')}
              value={startDate}
              onChange={date => setStartDate(date)}
              minDate={minDate}
              referenceDate={suggestedDate || dayjs()}
            />
            <DatePicker
              sx={{ width: '100%', svg: { color: 'text.mediumEmphasis' } }}
              slotProps={{ textField: { size: 'small' } }}
              label={endDateLabel ?? t('components.dateRangePickerWithPresets.dateRange.endDate')}
              value={endDate}
              onChange={date => setEndDate(date)}
              minDate={startDate}
              referenceDate={suggestedDate || dayjs()}
            />
          </Stack>
        )}
      </LocalizationProvider>
      {!isDownSm && (
        <Tooltip title={t('components.dateRangePickerWithPresets.dateRange.presetsTooltip')}>
          <SquareButton onClick={handleClick} sx={{ alignSelf: 'center' }} color="secondary">
            <FilterList />
          </SquareButton>
        </Tooltip>
      )}
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {presets.map(item => (
          <MenuItem key={item.key} onClick={() => applyPreset(item.key)} selected={selectedPreset === item.key}>
            {selectedPreset === item.key && <Check fontSize="small" />}
            {item.name}
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  )
}

export default DateRangePickerWithPresets
